import { useState, useRef, useCallback, useEffect } from "react";
import { Download, Share2, X, RefreshCw } from "lucide-react";
import { Button } from "./Button";
import { imageLibraryAPI, affiliateAPI } from "../services/api";
import QRCode from "qrcode";

/**
 * Share Alignment Card
 * Generates a beautiful shareable image card for Daily Alignment prompts
 * Features nature backgrounds from the Reclaim image library
 * Includes user's personal referral link for attribution
 */
export const ShareAlignmentCard = ({ 
  prompt,           // The alignment prompt object { text, category, type }
  stepType,         // "morning", "practice", or "evening"
  onClose,
  isVisible = false 
}) => {
  const canvasRef = useRef(null);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [logoImage, setLogoImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingBg, setIsLoadingBg] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [referralCode, setReferralCode] = useState(null);

  // Step labels for display
  const stepLabels = {
    morning: { label: "Morning Reflection", icon: "☀️" },
    practice: { label: "Midday Practice", icon: "✨" },
    evening: { label: "Evening Integration", icon: "🌙" }
  };

  const currentStep = stepLabels[stepType] || stepLabels.morning;

  // Load the Reclaim logo
  useEffect(() => {
    if (isVisible && !logoImage) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => setLogoImage(img);
      img.src = "/assets/reclaim-logo-white.png";
    }
  }, [isVisible, logoImage]);

  // Fetch user's referral code
  useEffect(() => {
    if (isVisible && !referralCode) {
      affiliateAPI.getCode()
        .then(res => setReferralCode(res.data?.code))
        .catch(() => {}); // Silently fail
    }
  }, [isVisible, referralCode]);

  // Fetch a nature background image
  const fetchBackgroundImage = useCallback(async () => {
    setIsLoadingBg(true);
    try {
      // Get random image from nature categories
      const categories = ["still_water", "golden_light", "mountain_silence", "forest_presence"];
      const randomCategory = categories[Math.floor(Math.random() * categories.length)];
      const res = await imageLibraryAPI.getRandom(randomCategory);
      
      if (res.data?.url) {
        // Preload the image
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          setBackgroundImage(img);
          setIsLoadingBg(false);
        };
        img.onerror = () => {
          // Fallback to a solid gradient if image fails
          setBackgroundImage(null);
          setIsLoadingBg(false);
        };
        img.src = res.data.url;
      } else {
        setBackgroundImage(null);
        setIsLoadingBg(false);
      }
    } catch (e) {
      console.error("Failed to fetch background:", e);
      setBackgroundImage(null);
      setIsLoadingBg(false);
    }
  }, []);

  // Load background on mount
  useEffect(() => {
    if (isVisible) {
      fetchBackgroundImage();
    }
  }, [isVisible, fetchBackgroundImage]);

  // Generate the card image on canvas
  const generateCard = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const ctx = canvas.getContext('2d');
    const width = 1080;
    const height = 1920;
    
    canvas.width = width;
    canvas.height = height;

    // Draw background image or gradient fallback
    if (backgroundImage) {
      // Draw image covering the canvas
      const imgRatio = backgroundImage.width / backgroundImage.height;
      const canvasRatio = width / height;
      
      let drawWidth, drawHeight, drawX, drawY;
      
      if (imgRatio > canvasRatio) {
        drawHeight = height;
        drawWidth = height * imgRatio;
        drawX = (width - drawWidth) / 2;
        drawY = 0;
      } else {
        drawWidth = width;
        drawHeight = width / imgRatio;
        drawX = 0;
        drawY = (height - drawHeight) / 2;
      }
      
      ctx.drawImage(backgroundImage, drawX, drawY, drawWidth, drawHeight);
      
      // Add dark overlay for text readability
      const overlay = ctx.createLinearGradient(0, 0, 0, height);
      overlay.addColorStop(0, 'rgba(0, 0, 0, 0.4)');
      overlay.addColorStop(0.3, 'rgba(0, 0, 0, 0.2)');
      overlay.addColorStop(0.7, 'rgba(0, 0, 0, 0.2)');
      overlay.addColorStop(1, 'rgba(0, 0, 0, 0.5)');
      ctx.fillStyle = overlay;
      ctx.fillRect(0, 0, width, height);
    } else {
      // Fallback gradient (sage to deep green)
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, '#4A6741');
      gradient.addColorStop(0.5, '#3D5636');
      gradient.addColorStop(1, '#2D4028');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    }

    // Add subtle grain texture
    ctx.globalAlpha = 0.05;
    for (let i = 0; i < 3000; i++) {
      ctx.fillStyle = Math.random() > 0.5 ? '#FFFFFF' : '#000000';
      ctx.fillRect(
        Math.random() * width,
        Math.random() * height,
        1,
        1
      );
    }
    ctx.globalAlpha = 1;

    // Top section - Logo and branding
    if (logoImage) {
      // Draw the actual logo image
      const logoHeight = 60;
      const logoWidth = (logoImage.width / logoImage.height) * logoHeight;
      const logoX = (width - logoWidth) / 2;
      const logoY = 120;
      ctx.drawImage(logoImage, logoX, logoY, logoWidth, logoHeight);
      
      // Tagline below logo
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.font = '400 22px "Inter", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('School of Consciousness', width / 2, logoY + logoHeight + 40);
      
      // Decorative line under tagline
      ctx.strokeStyle = 'rgba(212, 163, 115, 0.6)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(width / 2 - 80, logoY + logoHeight + 70);
      ctx.lineTo(width / 2 + 80, logoY + logoHeight + 70);
      ctx.stroke();
    } else {
      // Fallback to text if logo hasn't loaded
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.font = '600 52px "Playfair Display", Georgia, serif';
      ctx.textAlign = 'center';
      ctx.fillText('RECLAIM', width / 2, 180);
      
      // Tagline
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.font = '400 22px "Inter", sans-serif';
      ctx.fillText('School of Consciousness', width / 2, 225);

      // Decorative line under logo
      ctx.strokeStyle = 'rgba(212, 163, 115, 0.6)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(width / 2 - 80, 260);
      ctx.lineTo(width / 2 + 80, 260);
      ctx.stroke();
    }

    // Step type badge
    const badgeY = 700;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.beginPath();
    ctx.roundRect(width / 2 - 140, badgeY - 30, 280, 50, 25);
    ctx.fill();
    
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.font = '500 22px "Inter", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${currentStep.icon}  ${currentStep.label.toUpperCase()}`, width / 2, badgeY + 5);

    // Main prompt text
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '500 58px "Playfair Display", Georgia, serif';
    ctx.textAlign = 'center';
    
    // Word wrap the prompt
    const promptLines = wrapText(ctx, prompt?.text || "Take a moment to pause.", width - 140, 58);
    let yPos = 850;
    promptLines.forEach(line => {
      ctx.fillText(line, width / 2, yPos);
      yPos += 80;
    });

    // Category tag
    if (prompt?.category) {
      yPos += 40;
      ctx.fillStyle = 'rgba(212, 163, 115, 0.9)';
      ctx.font = '400 24px "Inter", sans-serif';
      const categoryDisplay = prompt.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      ctx.fillText(categoryDisplay, width / 2, yPos);
    }

    // Bottom section
    // Decorative line
    ctx.strokeStyle = 'rgba(212, 163, 115, 0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(width / 2 - 50, height - 320);
    ctx.lineTo(width / 2 + 50, height - 320);
    ctx.stroke();

    // "Shared from Reclaim" text
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.font = '400 20px "Inter", sans-serif';
    ctx.fillText('Shared from Reclaim', width / 2, height - 280);

    // Generate and draw QR code
    const qrUrl = referralCode 
      ? `${window.location.origin}/?ref=${referralCode}`
      : `${window.location.origin}`;
    
    try {
      const qrDataUrl = await QRCode.toDataURL(qrUrl, {
        width: 140,
        margin: 0,
        color: { dark: "#ffffff", light: "#00000000" }
      });
      
      const qrImg = new Image();
      qrImg.src = qrDataUrl;
      await new Promise((resolve, reject) => {
        qrImg.onload = resolve;
        qrImg.onerror = reject;
      });
      
      // Draw QR code
      ctx.drawImage(qrImg, width / 2 - 70, height - 250, 140, 140);
      
      // Referral code text below QR
      if (referralCode) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
        ctx.font = '500 18px "Inter", sans-serif';
        ctx.fillText(`Code: ${referralCode}`, width / 2, height - 80);
      }
    } catch (err) {
      // Fallback: just show URL text if QR fails
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.font = '500 24px "Inter", sans-serif';
      const displayUrl = referralCode ? `reclaim.app/?ref=${referralCode}` : 'reclaim.app';
      ctx.fillText(displayUrl, width / 2, height - 170);
    }

    return canvas.toDataURL('image/png');
  }, [backgroundImage, logoImage, prompt, currentStep, referralCode]);

  // Helper function for text wrapping
  const wrapText = (ctx, text, maxWidth, fontSize) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines;
  };

  // Update preview when background or prompt changes
  const updatePreview = useCallback(async () => {
    if (isLoadingBg) return;
    setIsGenerating(true);
    const url = await generateCard();
    setPreviewUrl(url);
    setIsGenerating(false);
  }, [generateCard, isLoadingBg]);

  // Generate preview when ready
  useEffect(() => {
    if (isVisible && !isLoadingBg) {
      updatePreview();
    }
  }, [isVisible, isLoadingBg, updatePreview]);

  // Refresh with new background
  const handleRefreshBackground = async () => {
    await fetchBackgroundImage();
  };

  // Download image
  const handleDownload = async () => {
    setIsGenerating(true);
    const dataUrl = await generateCard();
    
    const link = document.createElement('a');
    link.download = `reclaim-${stepType}-alignment.png`;
    link.href = dataUrl;
    link.click();
    
    setIsGenerating(false);
  };

  // Share via native share sheet
  const handleShare = async () => {
    setIsGenerating(true);
    const dataUrl = await generateCard();
    
    // Convert data URL to blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const file = new File([blob], `reclaim-${stepType}-alignment.png`, { type: 'image/png' });

    if (navigator.share && navigator.canShare({ files: [file] })) {
      try {
        const shareUrl = referralCode ? `https://reclaim.app/?ref=${referralCode}` : 'https://reclaim.app';
        await navigator.share({
          files: [file],
          title: 'Daily Alignment',
          text: `${prompt?.text || 'A moment of alignment'}\n\n${shareUrl}`
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Fallback to download if share fails
          handleDownload();
        }
      }
    } else {
      // Fallback for browsers without Web Share API
      handleDownload();
    }
    
    setIsGenerating(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
      <div className="bg-white rounded-3xl max-w-sm w-full max-h-[85vh] overflow-hidden shadow-2xl animate-fade-in-up flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB] flex-shrink-0">
          <h3 className="font-heading text-lg text-[#1F2937]">Share Your Alignment</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#F3F4F6] rounded-full transition-colors"
            data-testid="close-share-alignment-modal"
          >
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>

        {/* Preview - Scrollable content area */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <div className="relative aspect-[4/5] bg-gradient-to-b from-[#4A6741] to-[#2D4028] rounded-2xl overflow-hidden shadow-inner">
            {isLoadingBg ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-white/60 text-sm">Loading background...</p>
                </div>
              </div>
            ) : previewUrl ? (
              <img 
                src={previewUrl} 
                alt="Share card preview" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            
            {/* Refresh background button */}
            {!isLoadingBg && (
              <button
                onClick={handleRefreshBackground}
                disabled={isGenerating}
                className="absolute top-3 right-3 w-8 h-8 bg-white/20 backdrop-blur rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                title="New background"
                data-testid="refresh-background-btn"
              >
                <RefreshCw size={16} className="text-white" />
              </button>
            )}
          </div>

          {/* Prompt info */}
          <div className="p-3 bg-[#F9FAFB] rounded-xl space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-base">{currentStep.icon}</span>
              <span className="text-sm font-medium text-[#4A6741]">{currentStep.label}</span>
            </div>
            <p className="text-sm text-[#6B7280] line-clamp-2">
              {prompt?.text}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleDownload}
              disabled={isGenerating || isLoadingBg}
              className="flex-1"
              data-testid="download-alignment-btn"
            >
              <Download size={18} />
              Save
            </Button>
            <Button
              onClick={handleShare}
              disabled={isGenerating || isLoadingBg}
              className="flex-1"
              data-testid="share-alignment-btn"
            >
              <Share2 size={18} />
              Share
            </Button>
          </div>

          {/* Share destinations hint */}
          <div className="flex items-center justify-center gap-3 pt-1">
            <span className="text-xs text-[#9CA3AF]">Share to</span>
            <div className="flex items-center gap-2 text-[#6B7280]">
              <span className="text-xs">Instagram</span>
              <span className="text-[#D1D5DB]">•</span>
              <span className="text-xs">WhatsApp</span>
              <span className="text-[#D1D5DB]">•</span>
              <span className="text-xs">X</span>
            </div>
          </div>
        </div>

        {/* Hidden canvas for image generation */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
};

export default ShareAlignmentCard;
